/**
 * Send a genuinely new person to /welcome, once.
 *
 * "New" is answered by the server, not guessed: `GET /api/scheduling-preferences`
 * reports `answered` — the keys this person has actually set. Every value in
 * that response falls back to a default, so an empty `answered` is the only
 * way to tell "they chose 07:00" from "we assumed 07:00". On 2026-09-06 every
 * profile in production was in the second state and nothing ever said so.
 *
 * It is self-limiting three ways:
 *   - finishing or skipping through the flow writes the day shape, so
 *     `answered` stops being empty
 *   - "Don't ask again" at the door sets `welcome_declined`, which outranks
 *     everything else
 *   - `welcome_pending` re-offers it to someone who has already answered
 *     something, and is cleared by finishing or declining
 *   - a per-session flag stops a second ask in the same sitting, which is what
 *     makes "Not now" work with no server state at all: it simply means "not
 *     this session", and the invitation returns by itself next time
 *
 * A person who closes the tab halfway is offered it again, which is what you
 * would want.
 */
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

/** Checked once per tab; a redirect loop is worse than a missed welcome. */
const ASKED = 'first-run-checked'

/** Routes where a redirect would interrupt something the person chose to open. */
const LANDINGS = new Set(['/', '/today'])

export function useFirstRun(enabled = true) {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  useEffect(() => {
    if (!enabled || !LANDINGS.has(pathname)) return
    try {
      if (sessionStorage.getItem(ASKED)) return
    } catch {
      return // private mode and friends: never risk a loop
    }

    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/scheduling-preferences', { credentials: 'include' })
        if (!res.ok) return
        const body = await res.json()
        // An older server has no `answered`; treat that as "not new" so an
        // out-of-date deploy never bounces an established user to setup.
        if (!Array.isArray(body?.answered)) return
        try { sessionStorage.setItem(ASKED, '1') } catch { /* not essential */ }
        if (cancelled) return
        // Someone who closed this door stays out of it, on every device — a
        // decline outranks everything below, or the button would be a lie.
        if (body.welcome_declined) return
        // `welcome_pending` is an explicit re-offer, for people who have
        // already answered something (everyone who used the app before the
        // welcome existed). Without it the empty-`answered` gate can only ever
        // fire once per account.
        if (body.welcome_pending || body.answered.length === 0) {
          navigate('/welcome', { replace: true })
        }
      } catch {
        // Offline or a blip: staying put is always the safe answer.
      }
    })()
    return () => { cancelled = true }
  }, [enabled, pathname, navigate])
}
